/**
 * VÉRAULT Travels — Payments v2 (Phase 2 of Operational + Experience Pass)
 *
 * Money flow command center:
 *   - Top: AR / AP / All tab pills (mirrors the data model's `direction` field)
 *   - KPI strip (Outstanding, Overdue, Paid, Count)
 *   - Rollup view toggle: by client | by trip | by status
 *   - Payment list (filtered by tab) with checkboxes → bulk Mark Paid
 *   - Status pills colour-coded for paid/pending/overdue
 *   - Right rail not used here (page is data-heavy; rollups occupy the spotlight instead)
 *
 * VÉRAULT aesthetic — calm neutrals, serif headings, status pills only for state, never for
 * decoration.  No charts in Phase 2; numbers do the talking.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckSquare, Square, RefreshCw, ChevronRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(amount, currency = 'USD') {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(amount));
  } catch { return `$${Number(amount).toFixed(0)}`; }
}

function StatusPill({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-[#F0EDE6] text-[#6B6B6B]',
    success: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-800',
    danger:  'bg-red-50 text-red-700',
    info:    'bg-blue-50 text-blue-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

const TONE_FOR_STATUS = { paid: 'success', overdue: 'danger', pending: 'warning' };

export default function PaymentsPageV2() {
  const navigate = useNavigate();

  // Tab = direction filter.  'all' shows everything.
  const [direction, setDirection] = useState('all');           // all | receivable | payable
  const [groupBy, setGroupBy]     = useState('client');        // client | trip | status
  const [data, setData]           = useState({ payments: [], summary: {} });
  const [rollup, setRollup]       = useState({ rows: [] });
  const [clientMap, setClientMap] = useState({});              // client_id → name
  const [tripMap, setTripMap]     = useState({});              // trip_id → label
  const [selected, setSelected]   = useState(new Set());
  const [loading, setLoading]     = useState(true);
  const [busy, setBusy]           = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = direction === 'all' ? {} : { direction };
      const [pRes, rRes, cRes, tRes] = await Promise.all([
        axios.get(`${API}/payments`, { params, withCredentials: true }),
        axios.get(`${API}/payments/rollup`, { params: { group_by: groupBy, ...params }, withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/travels/trips`, { withCredentials: true }),
      ]);
      setData(pRes.data);
      setRollup(rRes.data);
      const cMap = {};
      (cRes.data || []).forEach(c => { cMap[c.id] = c.full_name || c.email; });
      setClientMap(cMap);
      const tMap = {};
      (tRes.data || []).forEach(t => {
        tMap[t.id] = t.internal_trip_name || t.client_trip_title || t.destinations || t.id;
      });
      setTripMap(tMap);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [direction, groupBy]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setSelected(new Set()); }, [direction]);   // clear selection on tab change

  const payments = data.payments || [];

  // Tab-scoped KPIs.  Backend summary returns everything for the current direction filter,
  // so we just read the matching fields.
  const kpis = useMemo(() => {
    const s = data.summary || {};
    if (direction === 'payable') {
      return {
        total:       s.total_payable || 0,
        outstanding: s.total_ap_outstanding || 0,
        paid:        s.total_paid_out || 0,
        overdue:     s.overdue_amount || 0,
        count:       payments.length,
      };
    }
    if (direction === 'receivable') {
      return {
        total:       s.total_receivable || 0,
        outstanding: s.total_ar_outstanding || 0,
        paid:        s.total_received || 0,
        overdue:     s.overdue_amount || 0,
        count:       payments.length,
      };
    }
    return {
      total:       (s.total_receivable || 0) + (s.total_payable || 0),
      outstanding: s.total_owed || 0,
      paid:        s.total_paid || 0,
      overdue:     s.overdue_amount || 0,
      count:       payments.length,
    };
  }, [direction, data.summary, payments.length]);

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => {
      const unpaid = payments.filter(p => (p.status || 'pending').toLowerCase() !== 'paid').map(p => p.id);
      if (prev.size >= unpaid.length) return new Set();
      return new Set(unpaid);
    });
  };

  const bulkMarkPaid = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/payments/bulk-mark-paid`, { payment_ids: Array.from(selected) }, { withCredentials: true });
      toast.success(`${res.data.updated} payment${res.data.updated === 1 ? '' : 's'} marked paid`);
      setSelected(new Set());
      fetchAll();
    } catch (err) {
      toast.error('Bulk mark failed');
    } finally {
      setBusy(false);
    }
  };

  const markOne = async (id) => {
    try {
      await axios.post(`${API}/payments/${id}/mark-paid`, {}, { withCredentials: true });
      toast.success('Marked paid');
      fetchAll();
    } catch { toast.error('Failed to mark paid'); }
  };

  const labelForRollupRow = (row) => {
    if (groupBy === 'client') return row.label || 'Unassigned';
    if (groupBy === 'trip')   return row.label || 'Unassigned';
    return row.label;
  };

  const rollupRowLink = (row) => {
    if (groupBy === 'client' && row.key && row.key !== 'unassigned') return `/advisor/manage/clients/${row.key}#money`;
    if (groupBy === 'trip'   && row.key && row.key !== 'unassigned') return `/advisor/trips/${row.key}`;
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="payments-v2-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-30 bg-[#FAFAF7]/95 backdrop-blur-md border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/advisor/manage')}
            className="text-[#8C8C8C] hover:text-[#1A1A1A]"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C8C8C]">Manage</p>
            <h1 className="font-serif text-xl md:text-2xl text-[#1A1A1A]">Money</h1>
          </div>
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            className="text-[11px] uppercase tracking-wider text-[#6B6B6B] hover:text-[#1A1A1A] flex items-center gap-1.5 disabled:opacity-50"
            data-testid="payments-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Direction tabs */}
        <div className="max-w-7xl mx-auto px-5 md:px-10 pb-3 flex gap-1 text-xs" data-testid="payments-dir-tabs">
          {[
            { v: 'all',        label: 'All' },
            { v: 'receivable', label: 'Receivable (AR)' },
            { v: 'payable',    label: 'Payable (AP)' },
          ].map(t => (
            <button
              key={t.v}
              type="button"
              onClick={() => setDirection(t.v)}
              data-testid={`payments-tab-${t.v}`}
              className={`px-3 py-1.5 rounded-full transition-colors ${
                direction === t.v ? 'bg-[#1A1A1A] text-white' : 'text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE6]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 space-y-10">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payments-kpis">
          <KpiCard label="Total"       value={money(kpis.total)} />
          <KpiCard label="Outstanding" value={money(kpis.outstanding)} tone={kpis.outstanding > 0 ? 'danger' : 'neutral'} />
          <KpiCard label="Paid"        value={money(kpis.paid)} tone="success" />
          <KpiCard label="Overdue"     value={money(kpis.overdue)} tone={kpis.overdue > 0 ? 'danger' : 'neutral'} />
        </div>

        {/* Rollup */}
        <section data-testid="payments-rollup">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-serif text-xl text-[#1A1A1A]">Rollup</h2>
            <div className="flex gap-1 text-[11px]" data-testid="payments-groupby-tabs">
              {[
                { v: 'client', label: 'By client' },
                { v: 'trip',   label: 'By trip' },
                { v: 'status', label: 'By status' },
              ].map(g => (
                <button
                  key={g.v}
                  type="button"
                  onClick={() => setGroupBy(g.v)}
                  data-testid={`payments-groupby-${g.v}`}
                  className={`px-2.5 py-1 rounded-full ${
                    groupBy === g.v ? 'bg-[#1A1A1A] text-white' : 'text-[#6B6B6B] hover:text-[#1A1A1A]'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
          {rollup.rows.length === 0 ? (
            <p className="text-sm text-[#8C8C8C] italic px-4 py-6 rounded-xl bg-[#F5F3ED]">No payments tracked yet.</p>
          ) : (
            <div className="rounded-xl border border-[#E8E4DC] bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-[#8C8C8C] bg-[#FBFAF6]">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-normal">
                      {groupBy === 'client' ? 'Client' : groupBy === 'trip' ? 'Trip' : 'Status'}
                    </th>
                    <th className="text-right px-3 py-2.5 font-normal">Total</th>
                    <th className="text-right px-3 py-2.5 font-normal">Paid</th>
                    <th className="text-right px-3 py-2.5 font-normal">Pending</th>
                    <th className="text-right px-3 py-2.5 font-normal">Overdue</th>
                    <th className="text-right px-4 py-2.5 font-normal">#</th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.rows.map(row => {
                    const link = rollupRowLink(row);
                    const labelEl = (
                      <span className="text-[#1A1A1A]">{labelForRollupRow(row)}</span>
                    );
                    return (
                      <tr key={row.key} className="border-t border-[#E8E4DC] hover:bg-[#FBFAF6]" data-testid={`payments-rollup-row-${row.key}`}>
                        <td className="px-4 py-2.5">
                          {link
                            ? <Link to={link} className="flex items-center gap-1 hover:underline">{labelEl} <ChevronRight className="w-3.5 h-3.5 text-[#999]" /></Link>
                            : labelEl}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{money(row.total)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{money(row.paid)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{money(row.pending)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-red-700">{money(row.overdue)}</td>
                        <td className="px-4 py-2.5 text-right text-[#8C8C8C]">{row.count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Bulk-action bar */}
        {selected.size > 0 && (
          <div className="sticky top-[140px] z-20 rounded-xl bg-[#1A1A1A] text-white px-4 py-3 flex items-center gap-4" data-testid="payments-bulk-bar">
            <CheckSquare className="w-4 h-4" />
            <p className="text-sm">{selected.size} selected</p>
            <button
              type="button"
              onClick={bulkMarkPaid}
              disabled={busy}
              data-testid="payments-bulk-mark-paid"
              className="ml-auto px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded text-xs uppercase tracking-wider disabled:opacity-50"
            >
              {busy ? 'Updating…' : 'Mark paid'}
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs text-[#999] hover:text-white"
            >
              Clear
            </button>
          </div>
        )}

        {/* Payment list */}
        <section data-testid="payments-list">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-serif text-xl text-[#1A1A1A]">Payments</h2>
            <span className="text-[11px] uppercase tracking-wider text-[#8C8C8C]">{payments.length} record{payments.length === 1 ? '' : 's'}</span>
          </div>

          {payments.length === 0 ? (
            <p className="text-sm text-[#8C8C8C] italic px-4 py-6 rounded-xl bg-[#F5F3ED]">No payments in this view.</p>
          ) : (
            <div className="rounded-xl border border-[#E8E4DC] bg-white overflow-hidden">
              <div className="px-4 py-2 flex items-center gap-3 border-b border-[#E8E4DC] bg-[#FBFAF6] text-[10px] uppercase tracking-wider text-[#8C8C8C]">
                <button type="button" onClick={toggleAll} className="text-[#6B6B6B] hover:text-[#1A1A1A]" aria-label="Select all">
                  {selected.size > 0 ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                </button>
                <span className="flex-1">Description</span>
                <span className="w-24 text-right">Due</span>
                <span className="w-16">Type</span>
                <span className="w-16">Status</span>
                <span className="w-24 text-right">Amount</span>
                <span className="w-20"></span>
              </div>
              {payments.map(p => {
                const status = (p.status || 'pending').toLowerCase();
                const isPaid = status === 'paid';
                const isSelected = selected.has(p.id);
                const clientName = p.client_id ? clientMap[p.client_id] : null;
                const tripLabel  = p.trip_id   ? tripMap[p.trip_id]      : null;
                return (
                  <div
                    key={p.id}
                    data-testid={`payments-row-${p.id}`}
                    className={`px-4 py-3 flex items-center gap-3 border-b border-[#E8E4DC] last:border-b-0 hover:bg-[#FBFAF6] ${isSelected ? 'bg-amber-50' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => !isPaid && toggleOne(p.id)}
                      disabled={isPaid}
                      data-testid={`payments-check-${p.id}`}
                      className={isPaid ? 'opacity-30 cursor-not-allowed' : 'text-[#6B6B6B] hover:text-[#1A1A1A]'}
                      aria-label={isSelected ? 'Deselect' : 'Select'}
                    >
                      {isSelected ? <CheckSquare className="w-3.5 h-3.5 text-amber-700" /> : <Square className="w-3.5 h-3.5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A1A1A] truncate">{p.description || p.payment_type || 'Payment'}</p>
                      <p className="text-[11px] text-[#8C8C8C] truncate">
                        {clientName && <Link to={`/advisor/manage/clients/${p.client_id}`} className="hover:underline">{clientName}</Link>}
                        {clientName && tripLabel && ' · '}
                        {tripLabel && <Link to={`/advisor/trips/${p.trip_id}`} className="hover:underline">{tripLabel}</Link>}
                      </p>
                    </div>
                    <span className="w-24 text-right text-xs text-[#6B6B6B]">{formatDate(p.due_date) || '—'}</span>
                    <span className="w-16">
                      <StatusPill tone={(p.direction || 'receivable') === 'payable' ? 'warning' : 'info'}>
                        {(p.direction || 'receivable') === 'payable' ? 'AP' : 'AR'}
                      </StatusPill>
                    </span>
                    <span className="w-16">
                      <StatusPill tone={TONE_FOR_STATUS[status] || 'neutral'}>{status}</StatusPill>
                    </span>
                    <span className="w-24 text-right text-sm text-[#1A1A1A] tabular-nums">{money(p.amount, p.currency)}</span>
                    <span className="w-20 text-right">
                      {!isPaid && (
                        <button
                          type="button"
                          onClick={() => markOne(p.id)}
                          data-testid={`payments-mark-${p.id}`}
                          className="text-[10px] uppercase tracking-wider text-emerald-700 hover:text-emerald-900"
                        >
                          Mark paid
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Legacy escape hatch retired in Phase 4 */}
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone = 'neutral' }) {
  const colors = {
    neutral: 'text-[#1A1A1A]',
    success: 'text-emerald-700',
    danger:  'text-red-700',
  };
  return (
    <div className="rounded-xl border border-[#E8E4DC] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C8C8C] mb-1">{label}</p>
      <p className={`font-serif text-2xl tabular-nums ${colors[tone]}`}>{value}</p>
    </div>
  );
}
